"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Search } from "lucide-react"
import {
    LayoutDashboard, Trophy, LineChart, BookOpen, Target, Zap, FileText,
    Dumbbell, Book, AlertCircle, User, HelpCircle, Shield, Activity, Users,
    PlusCircle, CheckSquare, BarChart, Swords, GraduationCap, Server, Video,
    PieChart, Bug, Coins, PenTool, AppWindow, type LucideIcon,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { cn } from "@/lib/utils"

const ROLE = { USER: 1, STUDENT: 2, MENTOR: 3, REVIEWER: 4, INSTRUCTOR: 5, ADMIN: 6, DEVELOPER: 7 }

interface CommandEntry {
    title: string
    href: string
    icon: LucideIcon
    section: string
    tag?: string
    minRole?: number
    keywords?: string[]
}

const allCommands: CommandEntry[] = [
    { title: "Home", href: "/dashboard", icon: LayoutDashboard, section: "Overview", keywords: ["dashboard"] },
    { title: "Leaderboard", href: "/dashboard/leaderboard", icon: Trophy, section: "Overview", keywords: ["ranking", "scores"] },
    { title: "Performance", href: "/dashboard/performance", icon: LineChart, section: "Overview", keywords: ["stats", "score", "progress"] },
    { title: "Universities", href: "/dashboard/universities", icon: GraduationCap, section: "Overview", keywords: ["college", "admission"] },
    { title: "Find Universities", href: "/tools/university-finder", icon: GraduationCap, section: "Tools", tag: "New", keywords: ["university", "college", "finder", "abroad", "scholarship", "admission", "us", "international"] },
    { title: "Practice", href: "/dashboard/practice", icon: Target, section: "Practice", keywords: ["questions", "drill"] },
    { title: "Quiz", href: "/dashboard/quiz", icon: Zap, section: "Practice", keywords: ["test", "quick"] },
    { title: "Mock Test", href: "/dashboard/mock-test", icon: FileText, section: "Practice", keywords: ["exam", "sat", "full test"] },
    { title: "Reading", href: "/dashboard/reading", icon: Book, section: "Practice", keywords: ["passage", "comprehension"] },
    { title: "Mistakes Log", href: "/dashboard/mistakes", icon: AlertCircle, section: "Practice", keywords: ["errors", "wrong", "review"] },
    { title: "My Courses", href: "/go-to-lms?to=/learn", icon: BookOpen, section: "Learning", keywords: ["lessons", "curriculum", "lms"] },
    { title: "Video Library", href: "/dashboard/video-library", icon: Video, section: "Learning", tag: "New", keywords: ["videos", "tutorials", "watch"] },
    { title: "Connections", href: "/dashboard/connections", icon: Users, section: "Social", keywords: ["friends", "follow"] },
    { title: "Mentors", href: "/dashboard/mentors", icon: Users, section: "Social", keywords: ["teacher", "tutor", "coach"] },
    { title: "Community", href: "/dashboard/community", icon: Users, section: "Social", keywords: ["forum", "discuss"] },
    { title: "Jamboard", href: "/dashboard/wall", icon: PenTool, section: "Social", tag: "New", keywords: ["whiteboard", "draw", "canvas"] },
    { title: "Vocab", href: "/dashboard/vocab", icon: Dumbbell, section: "Vocabulary", keywords: ["words", "definition"] },
    { title: "Vocab Battle", href: "/dashboard/vocab/game", icon: Swords, section: "Vocabulary", keywords: ["game", "compete", "multiplayer"] },
    { title: "Mentor Dashboard", href: "/dashboard/mentor", icon: LayoutDashboard, section: "Mentor", minRole: ROLE.MENTOR },
    { title: "Mentor Profile", href: "/dashboard/mentor/profile", icon: User, section: "Mentor", minRole: ROLE.MENTOR },
    { title: "Profile", href: "/dashboard/profile", icon: User, section: "Account", keywords: ["settings", "account", "edit"] },
    { title: "Earn Particle", href: "/dashboard/earn-particle", icon: Coins, section: "Account", tag: "New", keywords: ["coins", "rewards", "points"] },
    { title: "Report Bug", href: "/dashboard/report-bug", icon: Bug, section: "Account", keywords: ["issue", "problem", "feedback"] },
    { title: "Support", href: "/dashboard/support", icon: HelpCircle, section: "Account", keywords: ["help", "contact"] },
    { title: "Question Dashboard", href: "/admin/questions/dashboard", icon: BarChart, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Add Question", href: "/admin/questions/add", icon: PlusCircle, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Manage Questions", href: "/admin/questions", icon: FileText, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Reading Passages", href: "/admin/reading", icon: Book, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Review Questions", href: "/admin/review", icon: CheckSquare, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Vocab Editor", href: "/admin/vocab", icon: Dumbbell, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Skill Videos", href: "/admin/skill-videos", icon: Video, section: "Question Management", minRole: ROLE.REVIEWER },
    { title: "Admin Dashboard", href: "/admin/dashboard", icon: Shield, section: "Admin", minRole: ROLE.ADMIN },
    { title: "Analytics", href: "/admin/analytics", icon: PieChart, section: "Admin", minRole: ROLE.ADMIN },
    { title: "Student Monitor", href: "/admin/monitor", icon: Activity, section: "Admin", minRole: ROLE.ADMIN },
    { title: "API Monitor", href: "/admin/monitor/api", icon: Server, section: "Admin", minRole: ROLE.ADMIN },
    { title: "User Management", href: "/admin/users", icon: Users, section: "Admin", minRole: ROLE.ADMIN, keywords: ["manage users", "ban", "roles"] },
    { title: "Mentor Management", href: "/admin/mentors", icon: Users, section: "Admin", minRole: ROLE.ADMIN },
    { title: "Mock Tests Admin", href: "/admin/mock-tests", icon: FileText, section: "Admin", minRole: ROLE.ADMIN },
    { title: "Video Library Admin", href: "/admin/video-library", icon: Video, section: "Admin", minRole: ROLE.ADMIN },
    { title: "Finder Leads", href: "/admin/university-finder", icon: Target, section: "Admin", minRole: ROLE.ADMIN, keywords: ["university finder", "leads", "submissions", "public", "match score"] },
    { title: "Manage Bugs", href: "/admin/bugs", icon: Bug, section: "Developer", minRole: ROLE.DEVELOPER },
    { title: "Global References", href: "/admin/references", icon: BookOpen, section: "Developer", minRole: ROLE.DEVELOPER },
    { title: "Economy", href: "/admin/economy", icon: Coins, section: "Developer", minRole: ROLE.DEVELOPER },
    { title: "Membership", href: "/admin/membership", icon: Shield, section: "Developer", minRole: ROLE.DEVELOPER },
    { title: "Service Apps", href: "/admin/service-apps", icon: AppWindow, section: "Developer", minRole: ROLE.DEVELOPER },
]

interface CommandPaletteProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
    const router = useRouter()
    const { user } = useAuth()
    const userRole = user?.role ?? 0
    const [query, setQuery] = React.useState("")
    const [activeIndex, setActiveIndex] = React.useState(0)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const available = allCommands.filter(cmd => !cmd.minRole || userRole >= cmd.minRole)

    const results = React.useMemo(() => {
        if (!query.trim()) return available
        const q = query.toLowerCase()
        return available.filter(cmd =>
            cmd.title.toLowerCase().includes(q) ||
            cmd.section.toLowerCase().includes(q) ||
            cmd.keywords?.some(k => k.toLowerCase().includes(q))
        )
    }, [query, available])

    // Group results by section
    const grouped = React.useMemo(() => {
        const map: Record<string, CommandEntry[]> = {}
        for (const cmd of results) {
            if (!map[cmd.section]) map[cmd.section] = []
            map[cmd.section].push(cmd)
        }
        return Object.entries(map)
    }, [results])

    // Flat list for keyboard nav
    const flat = results

    React.useEffect(() => {
        setActiveIndex(0)
    }, [query])

    React.useEffect(() => {
        if (open) {
            setQuery("")
            setActiveIndex(0)
            setTimeout(() => inputRef.current?.focus(), 50)
        }
    }, [open])

    function handleSelect(href: string) {
        router.push(href)
        onOpenChange(false)
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "ArrowDown") {
            e.preventDefault()
            setActiveIndex(i => Math.min(i + 1, flat.length - 1))
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            setActiveIndex(i => Math.max(i - 1, 0))
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (flat[activeIndex]) handleSelect(flat[activeIndex].href)
        } else if (e.key === "Escape") {
            onOpenChange(false)
        }
    }

    // Global flat index tracker for keyboard highlight
    let flatIdx = 0

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="overflow-hidden p-0 shadow-2xl max-w-lg w-full rounded-xl" aria-describedby={undefined} onKeyDown={handleKeyDown}>
                <DialogTitle className="sr-only">Command Palette</DialogTitle>

                {/* Search input */}
                <div className="flex items-center gap-2 border-b px-4 py-3">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search features, pages, tools..."
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="text-muted-foreground hover:text-foreground text-xs"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Results */}
                <div className="max-h-[400px] overflow-y-auto p-2">
                    {grouped.length === 0 && (
                        <p className="py-8 text-center text-sm text-muted-foreground">No results found.</p>
                    )}
                    {grouped.map(([section, items]) => (
                        <div key={section} className="mb-1">
                            <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{section}</p>
                            {items.map(cmd => {
                                const idx = flatIdx++
                                const Icon = cmd.icon
                                const isActive = idx === activeIndex
                                return (
                                    <button
                                        key={cmd.href}
                                        onMouseDown={() => handleSelect(cmd.href)}
                                        onMouseEnter={() => setActiveIndex(idx)}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors",
                                            isActive
                                                ? "bg-accent text-accent-foreground"
                                                : "hover:bg-accent/50 text-foreground"
                                        )}
                                    >
                                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="flex-1">{cmd.title}</span>
                                        {cmd.tag && (
                                            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                                                {cmd.tag}
                                            </Badge>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </div>

                {/* Footer hint */}
                <div className="border-t px-4 py-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span><kbd className="font-mono">↑↓</kbd> navigate</span>
                    <span><kbd className="font-mono">↵</kbd> select</span>
                    <span><kbd className="font-mono">esc</kbd> close</span>
                </div>
            </DialogContent>
        </Dialog>
    )
}

